import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import { Loader } from '../components/ModalsCommonFunctions';
import { appMenuFormValidation } from '../validation/Validation';
import { set, ZodError } from 'zod';
import { ErrorMessage } from '../validation/validationError';

const MenuMaster = () => {
    const authorizedAxios = generateAxiosInstance()

    const [menuName, setMenuName] = useState('')
    const [menuList, setMenuList] = useState([])
    const [editId, setEditId] = useState(null)

    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);

    const [errors, setErrors] = useState([]);

    const [nameSort, setNameSort] = useState(null);
    const [menuIdSort, setMenuIdSort] = useState(null);
    const [startIndex, setStartIndex] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["menuName", "id"];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getMenuList();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            setCurrPage(1);
            setMenuList(await searchArray("A", '/get-menu', searchKeys, searchValue.trim()))
        }
    }

    useEffect(() => {
        getMenuList();
    }, []);

    const getMenuList = async () => {
        const apiUrl = `/get-menu`;
        try {
            const response = await authorizedAxios.get(apiUrl);
            setMenuList(response.data.result);
        } catch (error) {
            setMenuList([]);
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            console.error('Error fetching data:', errorMsg);
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(menuList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId])

    useEffect(() => {
        setTotalPages(Math.ceil(menuList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > menuList.length) {
            endIndex = menuList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(menuList.slice(startIndex, endIndex));
    }, [menuList, currPage, itemsPerPage]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMenuName(value);

        if (value.length !== 0) {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== name));
        }
    }

    const addMenu = async () => {
        const apiUrl = `/add-menu`;
        try {
            await appMenuFormValidation.parseAsync({ menuName });
            setErrors([]);
            setLoading(true);
            const response = await authorizedAxios.post(apiUrl, { menuName });

            await getMenuList();

            setSaveId(response.data.result.id);
            setMenuName('')
            setNameSort(null);
            setMenuIdSort(null);
            setSearchValue(null);

            Swal.fire(
                { text: response.data.message }
            );
        } catch (err) {
            if (err instanceof ZodError) {
                setErrors(err.errors);
            } else {
                const errorMsg = err?.response?.data?.message || 'Something went wrong!';
                Swal.fire({ text: errorMsg });
            }
        } finally {
            setLoading(false); // End loading
        }
    }

    const updateMenu = async () => {
        const apiUrl = `/edit-menu?id=${editId}`;

        try {
            await appMenuFormValidation.parseAsync({ menuName });
            setErrors([]);
            setLoading(true);
            const response = await authorizedAxios.put(apiUrl, { menuName });
            await getMenuList();
            setMenuName('')
            setEditId(null);
            setNameSort(null);
            setMenuIdSort(null);
            setSearchValue(null);

            Swal.fire(
                { text: response.data.message }
            );
        } catch (err) {
            if (err instanceof ZodError) {
                setErrors(err.errors);
            } else {
                const errorMsg = err?.response?.data?.message || 'Something went wrong!';
                Swal.fire({ text: errorMsg });
            }
        } finally {
            setLoading(false); // End loading
        }
    }

    const deleteMenu = async (id) => {
        const confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (!confirmed) return;

        setLoading(true);

        try {
            const apiUrl = `/delete-Menu?id=${id}`;
            const response = await authorizedAxios?.delete(apiUrl);

            const page = deleteItem(currPage, itemsPerPage, menuList.length);
            setCurrPage(page);

            Swal.fire({ text: response.data.message });

        } catch (error) {
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            Swal.fire({ text: errorMsg });

        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (nameSort == true || nameSort == false) {
            setMenuIdSort(null);
        }
    }, [nameSort])

    useEffect(() => {
        if (menuIdSort == true || menuIdSort == false) {
            setNameSort(null);
        }
    }, [menuIdSort])

    const handleInputReset = () => {
        setMenuName('');
        setEditId('');
        setMenuIdSort(null);
        setNameSort(null);
        setErrors([]);
        setSearchValue(null);
    }

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Menu Master</span></li>
                        </ol>
                    </nav>
                </div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fas fa-list"></i> Menu Master</b></h4>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">

                                                <div className="col-md-12 board_name">
                                                    <label for="inputPassword" className="col-form-label"><span style={{ color: "red" }}>*</span>Enter Menu Name</label>
                                                    <input type="text" className="form-control" id="menuName" name='menuName' value={menuName} onChange={handleInputChange} />
                                                    <ErrorMessage errors={errors} field="menuName" />
                                                </div>
                                            </div>
                                            <div className='row'>
                                                <div className="col-md-12 mt-3 d-flex justify-content-center button-container">
                                                    {!editId && <button className="btn btn-primary" type="button" onClick={addMenu}>Save</button>}
                                                    <button className="btn btn-danger delete_icon delete_icon" type="button" onClick={handleInputReset}>Reset</button>
                                                    {
                                                        editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateMenu} disabled={!editId}>Update</button>)
                                                    }
                                                </div>


                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}>{/*Search*/}<i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>

                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col" className='sl_no'>Sr. No.</th>
                                                                        <th scope="col">
                                                                            Menu Name
                                                                            <SortIcon sortState={nameSort} setSortState={setNameSort} setData={setMenuList} setCurrPage={setCurrPage} sortingPath={"menuName"} />
                                                                        </th>
                                                                        <th scope="col">
                                                                            Menu Id
                                                                            <SortIcon sortState={menuIdSort} setSortState={setMenuIdSort} setData={setMenuList} setCurrPage={setCurrPage} sortingPath={"id"} />
                                                                        </th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData.length > 0 ?
                                                                            displayedData.map((menu, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row">{startIndex + index + 1}</th>
                                                                                        <td>{menu.menuName}</td>
                                                                                        <td>{menu.id}</td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon" type="button" /*disabled={editId === menu.id}*/ onClick={() => {
                                                                                                setMenuName(menu.menuName);
                                                                                                setEditId(menu.id)
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>

                                                                                            {/* <button className="btn btn-outline-danger delete_icon" disabled={editId === menu.id} type="button" onClick={() => deleteMenu(menu.id)}>
                                                                                                <i class="fa-light fa-trash-can"></i>
                                                                                            </button> */}
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No Menu Available</th>
                                                                            </tr>)
                                                                    }
                                                                </tbody>
                                                            </table>

                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={menuList?.length} />

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default MenuMaster;